import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  carregarNavegacao,
  carregarPagina,
  urlDaImagem,
  type Bloco,
  type Navegacao,
  type Pagina,
  type SecaoNav,
} from "../lib/conteudo";
import { paraTextoSimples } from "../lib/textoRico";
import { useHub } from "../lib/dados";
import Carregando from "../componentes/Carregando";
import TextoRico from "../componentes/TextoRico";
import { SetaDireita } from "../componentes/ui";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Cabine, Numeral } from "../componentes/FerramentaUi";

// A tela das ferramentas de CONTEÚDO — genérica por card (000073).
//
// `ir-pro` é o primeiro morador. `mei-pro` entra com uma linha no despacho de
// `Ferramenta.tsx` e uma carga, sem nada aqui mudar: nada neste arquivo sabe o
// que é IR Pro. O nome e a descrição vêm do CARD, que a vitrine já entrega.
//
// ---------------------------------------------------------------------------
// A primeira versão desta tela ficou ruim, e o porquê vale registro
// ---------------------------------------------------------------------------
// Ela mostrava a barra lateral com o índice INTEIRO **e**, ao lado, o mesmo
// índice de novo em cards grandes — 34 assuntos duas vezes, numa página de 8.200
// pixels. Cada assunto era um bloco alto com o texto encolhido à esquerda e uma
// seta gigante ao lado (a seta era um defeito à parte: `SetaDireita` recebia
// `className` sem tamanho, e SVG sem dimensão estica para preencher o pai).
//
// O que ficou no lugar, e a razão de cada escolha:
//
//   * **Sumário e leitura são telas diferentes.** Na entrada, o índice É o
//     conteúdo, e ocupa a largura toda em duas colunas — cabe em uma tela e meia
//     em vez de oito. Lendo um assunto, aparece a barra lateral e o índice sai do
//     meio. Índice em dois lugares ao mesmo tempo é o que fazia a tela parecer
//     cheia sem dizer nada a mais.
//   * **Assunto é LINHA de texto, não card.** São 34 títulos curtos; card por
//     título gasta 120px de altura para mostrar 30 caracteres. Lista densa é o que
//     deixa "Day Trade" ser encontrado com o olho, que é o uso real: o contador
//     chega procurando um assunto específico, na temporada.
//   * **Nada de seta por item.** Em lista de links a seta não informa nada — todo
//     item leva a algum lugar. Ela volta só no par anterior/próximo, onde a direção
//     é a informação.
//   * **A barra lateral abre só a área atual.** Aberta inteira ela tem 34 itens e
//     não cabe na altura da tela, e foi por isso que na versão anterior ela
//     aparecia cortada no meio de "Exterior".
//
// ---------------------------------------------------------------------------
// A segunda versão (26/08) ficou "muito simples", disse Victor — e o diagnóstico
// ---------------------------------------------------------------------------
// A captura do antes (`.capturas/antes/03-…`): ícone, título, "7 áreas · 27
// assuntos" e uma caixa branca com duas colunas de texto. Correta em tudo o que
// a seção acima decidiu, e sem uma única peça bonita do hub — zero navy, zero
// número, zero imagem. O mesmo diagnóstico da primeira versão do HUB DP.
//
// O que entrou, sem desfazer nenhuma decisão de cima:
//
//   * **A cabine navy** (`Cabine`), com fatos REAIS em cartão de vidro: áreas,
//     assuntos, e — quando há — "você já leu N". E a ação principal é
//     **continuar de onde parou**: este navegador lembra o último assunto lido
//     (`localStorage`), e conteúdo de referência é reaberto no meio da leitura.
//     Sem histórico, a ação é começar pelo primeiro assunto.
//   * **Numeral de revista em cada área** (`01`, `02`…). A lista continua sendo
//     lista — o numeral é o que dá identidade a uma coluna de texto sem
//     transformá-la em grade.
//   * **A marca de LIDO** ao lado dos assuntos que este navegador já abriu. É
//     dado real (do próprio navegador), e é o que faz um sumário de 40 assuntos
//     responder "onde eu estava?".
//   * **A capa do artigo**: a área, a posição na sequência ("assunto 7 de 27") e
//     o tempo de leitura estimado — todos derivados do que a página já tem.
//
// O que NÃO entrou: barra de "progresso do curso", streak, ou qualquer número
// que não venha de dado. A regra do CLAUDE.md é a mesma de sempre.
//
// ---------------------------------------------------------------------------
// A rota é o endereço que o aluno compartilha
// ---------------------------------------------------------------------------
// `/ferramenta/ir-pro/renda-variavel/como-declarar-acoes`. Conteúdo sem link
// próprio é conteúdo que ninguém manda para o colega — e a equipe precisa linkar
// um artigo de dentro de uma aula. Daí a seção e a página estarem na URL, e não em
// estado da tela. O primeiro segmento continua sendo o slug do card, então a
// derivação da 000065 ("a rota vem do slug, e está num lugar só") segue valendo.

// `painel` é o único ponto de extensão: um bloco opcional que aparece SÓ no
// sumário, entre a cabine e o índice. Existe porque o MEI Pro tem uma metade
// que não é conteúdo (a gestão de clientes) e precisa de porta na entrada — e a
// alternativa seria esta tela genérica conhecer cada ferramenta, que é
// exatamente o que ela não pode fazer.
export default function FerramentaDeConteudo({
  card,
  painel,
}: {
  card: string;
  painel?: React.ReactNode;
}) {
  const { "*": resto = "" } = useParams();
  const hub = useHub();
  const [nav, setNav] = useState<Navegacao | null>(null);
  const [pagina, setPagina] = useState<Pagina | null>(null);
  const [carregandoPagina, setCarregandoPagina] = useState(false);
  const [lidos, setLidos] = useState<Set<string>>(() => lerLidos(card));
  const [ultima, setUltima] = useState<UltimaLeitura | null>(() => lerUltima(card));

  const [secaoSlug, paginaSlug] = useMemo(() => {
    const partes = resto.split("/").filter(Boolean);
    return [partes[0] ?? null, partes[1] ?? null];
  }, [resto]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const r = await carregarNavegacao(card);
      if (ativo) setNav(r);
    })();
    return () => {
      ativo = false;
    };
  }, [card]);

  useEffect(() => {
    if (!secaoSlug || !paginaSlug) {
      setPagina(null);
      return;
    }
    let ativo = true;
    setCarregandoPagina(true);
    (async () => {
      const r = await carregarPagina(card, secaoSlug, paginaSlug);
      if (!ativo) return;
      setPagina(r);
      setCarregandoPagina(false);
      // Trocar de assunto pela barra lateral tem de levar ao começo do texto. Sem
      // isto o aluno clica no item de baixo e continua no meio do artigo anterior,
      // parecendo que nada aconteceu.
      window.scrollTo({ top: 0, behavior: "instant" });
      // A memória de leitura, só quando a página EXISTE: guardar um link torto
      // como "última leitura" mandaria o aluno de volta a uma tela de "não
      // disponível".
      if (r.existe && r.nome && r.secao_nome) {
        const registro = { secao: secaoSlug, pagina: paginaSlug, nome: r.nome, secaoNome: r.secao_nome };
        setUltima(registro);
        setLidos(marcarLido(card, `${secaoSlug}/${paginaSlug}`, registro));
      }
    })();
    return () => {
      ativo = false;
    };
  }, [card, secaoSlug, paginaSlug]);

  if (!nav || hub.fase === "carregando") return <Carregando />;

  const info =
    hub.fase === "pronto"
      ? [...hub.hub.cardsDeCurso, ...hub.hub.cardsDeConta].find((c) => c.card_slug === card)
      : undefined;

  // A ferramenta existe e o aluno chegou até aqui, mas o banco disse não. Só
  // acontece com link antigo ou rota digitada — a tela do curso não mostra o card.
  if (!nav.permitido) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
        <h1 className="font-display text-2xl font-bold text-titulo">Conteúdo não disponível</h1>
        <p className="mt-3 text-sm leading-relaxed text-cinza">
          Este material faz parte de um curso que você não tem hoje, ou que está aguardando
          assinatura de contrato.
        </p>
        <Link to="/" className="pill-cta mt-6 inline-flex">
          Voltar para o início
        </Link>
      </main>
    );
  }

  const lendo = Boolean(secaoSlug && paginaSlug);
  const totalDePaginas = nav.secoes.reduce((a, s) => a + s.paginas.length, 0);
  const nome = info?.card_nome ?? "Conteúdo";
  const descricao = info?.descricao ?? "O manual do módulo, por assunto.";

  if (lendo) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
        {/* Só a volta ao SUMÁRIO daqui: quem volta ao curso é a `BarraDeVolta` da
            `Moldura`, uma vez, para toda ferramenta (pedido de Victor em 26/08).
            O texto "Voltar à Escola" que ficava aqui era cravado — falso para
            quem abre a ferramenta por uma pós. */}
        <Link to={`/ferramenta/${card}`} className="text-sm font-semibold text-link hover:underline">
          ‹ {nome}
        </Link>
        <div className="mt-5 gap-8 lg:flex">
          <Indice secoes={nav.secoes} card={card} secaoAtual={secaoSlug} paginaAtual={paginaSlug} lidos={lidos} />
          <div className="min-w-0 flex-1">
            {carregandoPagina ? (
              <div className="py-20">
                <Carregando />
              </div>
            ) : pagina?.existe ? (
              <>
                <Artigo pagina={pagina} secoes={nav.secoes} secao={secaoSlug!} paginaSlug={paginaSlug!} />
                <Vizinhas secoes={nav.secoes} card={card} secao={secaoSlug!} pagina={paginaSlug!} />
              </>
            ) : (
              <div className="surface p-6">
                {/* Sem "na lista ao lado": no celular a lista está atrás do botão
                    "Todos os assuntos", e não ao lado de nada. O link abaixo é o
                    caminho de volta que funciona nas duas larguras. */}
                <p className="text-sm leading-relaxed text-cinza">
                  Este assunto não está disponível. Ele pode ter mudado de endereço, ou ainda
                  estar sendo preparado.
                </p>
                <Link
                  to={`/ferramenta/${card}`}
                  className="mt-3 inline-flex text-sm font-semibold text-link underline decoration-link/30 underline-offset-4"
                >
                  Ver todas as áreas
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  // --- o sumário ---
  const primeira = nav.secoes.find((s) => s.paginas.length > 0);
  const continuar =
    ultima && nav.secoes.some((s) => s.slug === ultima.secao && s.paginas.some((p) => p.slug === ultima.pagina))
      ? ultima
      : null;
  const lidosValidos = [...lidos].filter((chave) => {
    const [s, p] = chave.split("/");
    return nav.secoes.some((x) => x.slug === s && x.paginas.some((y) => y.slug === p));
  }).length;

  return (
    <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Conteúdo"
        titulo={nome}
        descricao={descricao}
        acao={
          nav.secoes.length > 0 &&
          (continuar ? (
            <Link
              to={`/ferramenta/${card}/${continuar.secao}/${continuar.pagina}`}
              className="pill-cta text-sm"
              data-continuar
            >
              Continuar: {continuar.nome}
            </Link>
          ) : (
            primeira && (
              <Link
                to={`/ferramenta/${card}/${primeira.slug}/${primeira.paginas[0].slug}`}
                className="pill-cta text-sm"
              >
                {/* "Começar: …", no molde de "Continuar: …" — a primeira versão
                    dizia "Começar por Por que atender MEI", e o "por Por"
                    tropeça. Título de assunto começa com qualquer palavra; o
                    dois-pontos não depende dela. */}
                Começar: {primeira.paginas[0].nome}
              </Link>
            )
          ))
        }
        fatos={
          nav.secoes.length > 0 && (
            <>
              <TileDeFato rotulo="Áreas" valor={nav.secoes.length} />
              <TileDeFato rotulo="Assuntos" valor={totalDePaginas} />
              {lidosValidos > 0 && (
                <TileDeFato
                  rotulo="Você já leu"
                  valor={lidosValidos}
                  apoio={`de ${totalDePaginas}, neste navegador`}
                />
              )}
            </>
          )
        }
      />

      {painel}

      {nav.secoes.length === 0 ? (
        <p className="mt-8 rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          O conteúdo desta ferramenta está em preparação. Ele aparece aqui assim que a equipe
          publicar.
        </p>
      ) : (
        <Sumario secoes={nav.secoes} card={card} secaoFoco={secaoSlug} lidos={lidos} />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// A memória de leitura deste navegador
// ---------------------------------------------------------------------------
// `localStorage`, como o rascunho do Legaliza e o checklist do HUB DP: dado de
// conveniência do próprio navegador, que pode sumir e a tela continua certa sem
// ele. Não vai para o banco de propósito — "progresso" no servidor seria a
// segunda definição de uma coisa que o Memberkit já mede para as aulas.

type UltimaLeitura = { secao: string; pagina: string; nome: string; secaoNome: string };

const CHAVE_LIDOS = (card: string) => `cf-conteudo-lidos:${card}`;
const CHAVE_ULTIMA = (card: string) => `cf-conteudo-ultima:${card}`;

function lerLidos(card: string): Set<string> {
  try {
    const bruto = localStorage.getItem(CHAVE_LIDOS(card));
    const lista = bruto ? (JSON.parse(bruto) as unknown) : [];
    return new Set(Array.isArray(lista) ? lista.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function lerUltima(card: string): UltimaLeitura | null {
  try {
    const bruto = localStorage.getItem(CHAVE_ULTIMA(card));
    if (!bruto) return null;
    const u = JSON.parse(bruto) as Partial<UltimaLeitura>;
    if (typeof u.secao !== "string" || typeof u.pagina !== "string" || typeof u.nome !== "string") return null;
    return { secao: u.secao, pagina: u.pagina, nome: u.nome, secaoNome: typeof u.secaoNome === "string" ? u.secaoNome : "" };
  } catch {
    return null;
  }
}

function marcarLido(card: string, chave: string, ultima: UltimaLeitura): Set<string> {
  const atual = lerLidos(card);
  atual.add(chave);
  try {
    localStorage.setItem(CHAVE_LIDOS(card), JSON.stringify([...atual]));
    localStorage.setItem(CHAVE_ULTIMA(card), JSON.stringify(ultima));
  } catch {
    /* sem armazenamento: a tela segue sem memória */
  }
  return atual;
}

// ---------------------------------------------------------------------------
// O sumário — a tela de entrada
// ---------------------------------------------------------------------------
/**
 * DUAS colunas de multi-column CSS, e não grid nem três colunas.
 *
 * Grid está fora porque deixaria as áreas com altura igual, e elas têm de 1 a 11
 * assuntos: sairia uma fileira de caixas com muito vazio embaixo das curtas.
 *
 * Duas e não três porque `break-inside-avoid` não deixa "Renda Variável" (11
 * assuntos) ser cortada, e em três colunas o balanceador não tem o que fazer: a
 * primeira coluna terminava com 300px de branco. Em duas, as 34 unidades de altura
 * (7 títulos + 27 assuntos) caem em 19 e 15 — e a coluna mais larga faz "Títulos
 * Públicos - Tesouro Direto (LTN, NTN, LFT)" caber numa linha em vez de duas.
 * Menos colunas ficou mais calmo E mais equilibrado.
 */
function Sumario({
  secoes,
  card,
  secaoFoco,
  lidos,
}: {
  secoes: SecaoNav[];
  card: string;
  secaoFoco: string | null;
  lidos: Set<string>;
}) {
  // Rota com área e sem assunto (`/ferramenta/ir-pro/renda-fixa`): mostra só
  // aquela área. É link que alguém guardou ou digitou, e responder com o sumário
  // inteiro faria a URL parecer ignorada.
  const foco = secaoFoco ? secoes.find((s) => s.slug === secaoFoco) : null;
  const mostrar = foco ? [foco] : secoes;

  return (
    <>
      {foco && (
        <div className="mt-7 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm text-cinza">
            <span className="font-semibold text-tinta">{foco.nome}</span> · {foco.paginas.length}{" "}
            {foco.paginas.length === 1 ? "assunto" : "assuntos"}
          </p>
          <Link
            to={`/ferramenta/${card}`}
            className="text-sm font-semibold text-link underline decoration-link/30 underline-offset-4 hover:decoration-link"
          >
            Ver todas as áreas
          </Link>
        </div>
      )}

      <div className={`surface p-6 sm:p-8 ${foco ? "mt-3" : "mt-6"}`}>
        <div className={foco ? "" : "gap-10 sm:columns-2"}>
          {mostrar.map((s, i) => {
            const numero = foco ? secoes.indexOf(s) + 1 : i + 1;
            const lidosNaArea = s.paginas.filter((p) => lidos.has(`${s.slug}/${p.slug}`)).length;
            return (
              <section key={s.slug} className="mb-8 break-inside-avoid last:mb-0">
                <h2 className="flex items-baseline gap-3 border-b border-borda pb-2">
                  <Numeral n={numero} />
                  <span className="font-display text-base font-bold text-titulo">{s.nome}</span>
                  <span className="ml-auto text-xs font-semibold text-cinza tabular-nums">
                    {lidosNaArea > 0 ? `${lidosNaArea}/` : ""}
                    {s.paginas.length}
                  </span>
                </h2>
                <ul className="mt-1">
                  {s.paginas.map((p) => {
                    const lido = lidos.has(`${s.slug}/${p.slug}`);
                    return (
                      <li key={p.slug}>
                        <Link
                          to={`/ferramenta/${card}/${s.slug}/${p.slug}`}
                          className="-mx-2 flex min-h-9 items-center gap-2 rounded-lg px-2 py-1.5 text-sm leading-snug text-tinta transition hover:bg-chip/60 hover:text-link"
                        >
                          {/* A marca de lido: um ponto aceso, com o texto só para
                              leitor de tela. Cor sozinha nunca carrega informação. */}
                          <span
                            aria-hidden="true"
                            className={`size-1.5 shrink-0 rounded-full ${lido ? "bg-sucesso" : "bg-borda"}`}
                          />
                          {lido && <span className="sr-only">lido: </span>}
                          <span className="min-w-0 flex-1">{p.nome}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// A barra lateral — só na leitura
// ---------------------------------------------------------------------------
function Indice({
  secoes,
  card,
  secaoAtual,
  paginaAtual,
  lidos,
}: {
  secoes: SecaoNav[];
  card: string;
  secaoAtual: string | null;
  paginaAtual: string | null;
  lidos: Set<string>;
}) {
  // Abre a área atual, e mantém aberta a que o aluno abrir à mão. Aberta inteira
  // são 34 itens, que não cabem na altura da tela — foi como a primeira versão
  // desta tela apareceu cortada no meio de "Exterior".
  const [abertas, setAbertas] = useState<string[]>(secaoAtual ? [secaoAtual] : []);
  useEffect(() => {
    if (secaoAtual) setAbertas((a) => (a.includes(secaoAtual) ? a : [...a, secaoAtual]));
  }, [secaoAtual]);

  const [aberturaNoCelular, setAberturaNoCelular] = useState(false);

  return (
    <nav
      aria-label="Assuntos"
      className="mb-6 shrink-0 lg:sticky lg:top-6 lg:mb-0 lg:max-h-[calc(100dvh-3rem)] lg:w-72 lg:overflow-y-auto"
    >
      {/* No celular a lista fica fechada por padrão: ela empurraria o artigo para
          baixo da dobra, e quem abriu um link direto veio para LER, não para
          navegar. No desktop ela é sempre visível. */}
      <button
        type="button"
        onClick={() => setAberturaNoCelular((v) => !v)}
        aria-expanded={aberturaNoCelular}
        className="btn-contorno flex w-full items-center justify-between lg:hidden"
      >
        <span>Todos os assuntos</span>
        <span aria-hidden="true">{aberturaNoCelular ? "▲" : "▼"}</span>
      </button>

      <div className={`${aberturaNoCelular ? "mt-3 block" : "hidden"} lg:mt-0 lg:block`}>
        <div className="surface p-3">
          {secoes.map((s, i) => {
            const aberta = abertas.includes(s.slug);
            const atual = s.slug === secaoAtual;
            return (
              <div key={s.slug} className="border-b border-borda py-1 last:border-0">
                <button
                  type="button"
                  onClick={() =>
                    setAbertas((a) =>
                      a.includes(s.slug) ? a.filter((x) => x !== s.slug) : [...a, s.slug],
                    )
                  }
                  aria-expanded={aberta}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-chip/50"
                >
                  <span
                    aria-hidden="true"
                    className={`w-5 shrink-0 font-display text-xs font-bold tabular-nums ${
                      atual ? "text-laranja-texto" : "text-cinza/70"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`min-w-0 flex-1 text-xs font-bold tracking-wide uppercase ${
                      atual ? "text-link" : "text-cinza"
                    }`}
                  >
                    {s.nome}
                  </span>
                  <span className="text-[11px] text-cinza tabular-nums">{s.paginas.length}</span>
                </button>

                {aberta && (
                  <ul className="mt-0.5 mb-1 ml-4 border-l border-borda pl-2">
                    {s.paginas.map((p) => {
                      const aqui = atual && p.slug === paginaAtual;
                      const lido = lidos.has(`${s.slug}/${p.slug}`);
                      return (
                        <li key={p.slug}>
                          <Link
                            to={`/ferramenta/${card}/${s.slug}/${p.slug}`}
                            aria-current={aqui ? "page" : undefined}
                            onClick={() => setAberturaNoCelular(false)}
                            className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm leading-snug transition ${
                              aqui
                                ? "bg-tile font-semibold text-link"
                                : "text-tinta hover:bg-chip/60"
                            }`}
                          >
                            <span
                              aria-hidden="true"
                              className={`size-1.5 shrink-0 rounded-full ${lido || aqui ? "bg-sucesso" : "bg-borda"}`}
                            />
                            <span className="min-w-0 flex-1">{p.nome}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// O artigo
// ---------------------------------------------------------------------------
function Artigo({
  pagina,
  secoes,
  secao,
  paginaSlug,
}: {
  pagina: Pagina;
  secoes: SecaoNav[];
  secao: string;
  paginaSlug: string;
}) {
  const blocos = pagina.blocos ?? [];

  /**
   * Índice interno, só quando vale a pena.
   *
   * Há página com 110 blocos e 20 subtítulos ("Apuração Passo a Passo"), e nela
   * rolar até achar o passo 7 é o trabalho todo. Abaixo de cinco subtítulos o
   * índice é ruído — a página inteira já cabe no olho.
   */
  const ancoras = useMemo(
    () =>
      blocos
        .map((b, i) => ({ b, i }))
        .filter(({ b }) => b.tipo === "titulo")
        .map(({ b, i }) => ({ id: `bloco-${i}`, texto: paraTextoSimples(b.texto ?? "") })),
    [blocos],
  );

  // A capa: posição na sequência, tempo de leitura e imagens — tudo DERIVADO do
  // que a página já tem. Tempo de leitura a 200 palavras por minuto, que é a
  // convenção para texto técnico em português; é estimativa, e a tela diz "~".
  const capa = useMemo(() => {
    const plana = secoes.flatMap((s) => s.paginas.map((p) => `${s.slug}/${p.slug}`));
    const posicao = plana.indexOf(`${secao}/${paginaSlug}`) + 1;
    const numeroDaArea = secoes.findIndex((s) => s.slug === secao) + 1;
    const palavras = blocos.reduce((n, b) => {
      const textos = [b.texto ?? "", ...(b.itens ?? [])];
      return n + textos.join(" ").split(/\s+/).filter(Boolean).length;
    }, 0);
    const imagens = blocos.filter((b) => b.tipo === "imagem").length;
    const videos = blocos.filter((b) => b.tipo === "video").length;
    return { posicao, total: plana.length, numeroDaArea, minutos: Math.max(1, Math.round(palavras / 200)), imagens, videos };
  }, [blocos, secoes, secao, paginaSlug]);

  return (
    <article className="surface overflow-hidden">
      {/* A capa do artigo: a área com o seu numeral, o título, e os fatos de
          leitura. Faixa clara com o filete laranja à esquerda — NÃO navy: o
          artigo é para ler, e a leitura longa pede fundo claro (light-first). */}
      <header className="border-l-4 border-l-laranja bg-superficie-2 px-6 py-5 sm:px-8">
        <p className="flex items-center gap-2 text-xs font-bold tracking-wide text-cinza uppercase">
          <span className="font-display text-base text-laranja-texto/80 tabular-nums">
            {String(capa.numeroDaArea).padStart(2, "0")}
          </span>
          {pagina.secao_nome}
        </p>
        <h2 className="font-display mt-1 text-2xl leading-tight font-bold text-titulo sm:text-3xl">
          {pagina.nome}
        </h2>
        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-cinza tabular-nums">
          {capa.posicao > 0 && (
            <span>
              Assunto {capa.posicao} de {capa.total}
            </span>
          )}
          <span>~{capa.minutos} min de leitura</span>
          {capa.imagens > 0 && <span>{capa.imagens} {capa.imagens === 1 ? "imagem" : "imagens"}</span>}
          {capa.videos > 0 && <span>{capa.videos} {capa.videos === 1 ? "vídeo" : "vídeos"}</span>}
        </p>
      </header>

      <div className="p-6 sm:p-8">
        {ancoras.length >= 5 && (
          <nav aria-label="Nesta página" className="mb-6 rounded-xl bg-superficie-2 p-4">
            <p className="text-xs font-bold tracking-wide text-cinza uppercase">Nesta página</p>
            <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
              {ancoras.map((a) => (
                <li key={a.id}>
                  <a
                    href={`#${a.id}`}
                    className="block truncate py-0.5 text-sm text-link hover:underline"
                  >
                    {a.texto}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/*
          `max-w-[68ch]` limita a MEDIDA do texto, não a largura do card. Legislação
          tributária em linha de 120 caracteres é onde o olho perde a linha na volta;
          as imagens e os vídeos ficam fora do limite, porque print de tela cortado
          não se lê.
        */}
        <div className="space-y-5">
          {blocos.map((b, i) => (
            <BlocoRenderizado key={i} bloco={b} id={`bloco-${i}`} />
          ))}
        </div>
      </div>
    </article>
  );
}

/** O par anterior/próximo, na sequência de TODAS as áreas. */
function Vizinhas({
  secoes,
  card,
  secao,
  pagina,
}: {
  secoes: SecaoNav[];
  card: string;
  secao: string;
  pagina: string;
}) {
  const plana = useMemo(
    () =>
      secoes.flatMap((s) =>
        s.paginas.map((p) => ({
          href: `/ferramenta/${card}/${s.slug}/${p.slug}`,
          nome: p.nome,
          secao: s.nome,
          chave: `${s.slug}/${p.slug}`,
        })),
      ),
    [secoes, card],
  );

  const i = plana.findIndex((x) => x.chave === `${secao}/${pagina}`);
  if (i === -1) return null;
  const anterior = i > 0 ? plana[i - 1] : null;
  const proxima = i < plana.length - 1 ? plana[i + 1] : null;
  if (!anterior && !proxima) return null;

  return (
    // A sequência atravessa as áreas de propósito: o material foi escrito para ser
    // lido em ordem, e parar no fim de "Renda Fixa" obrigaria a voltar ao sumário
    // para continuar. O rótulo diz a área, então ninguém se perde ao trocar.
    <nav aria-label="Navegar entre assuntos" className="mt-4 grid gap-3 sm:grid-cols-2">
      {anterior ? (
        <Link
          to={anterior.href}
          className="surface porta group flex items-center gap-3 p-4"
        >
          <SetaDireita className="size-4 shrink-0 rotate-180 text-cinza transition group-hover:text-link" />
          <span className="min-w-0">
            <span className="block text-xs text-cinza">Anterior · {anterior.secao}</span>
            <span className="block text-sm font-semibold text-tinta group-hover:text-link">
              {anterior.nome}
            </span>
          </span>
        </Link>
      ) : (
        <span />
      )}
      {proxima && (
        <Link
          to={proxima.href}
          className="surface porta group flex items-center justify-end gap-3 p-4 text-right"
        >
          <span className="min-w-0">
            <span className="block text-xs text-cinza">Próximo · {proxima.secao}</span>
            <span className="block text-sm font-semibold text-tinta group-hover:text-link">
              {proxima.nome}
            </span>
          </span>
          <SetaDireita className="size-4 shrink-0 text-cinza transition group-hover:text-link" />
        </Link>
      )}
    </nav>
  );
}

const TONS: Record<string, string> = {
  info: "bg-tile text-tinta border-azul-medio",
  atencao: "bg-alerta-fundo text-alerta-tinta border-laranja",
  positivo: "bg-aviso-fundo text-aviso-tinta border-sucesso",
  neutro: "bg-superficie-2 text-tinta border-borda",
};

/** Um bloco do motor, desenhado. Exportado para o curso que mora no hub (000375) usar o MESMO desenho:
 * duas cópias divergiriam, e a que divergisse seria a do curso, que ninguém compara com o IR Pro. */
export function BlocoRenderizado({ bloco, id }: { bloco: Bloco; id: string }) {
  switch (bloco.tipo) {
    case "titulo":
      // `h3` porque o `h1` é o nome da ferramenta e o `h2` é o da página. Pular
      // nível quebra a navegação por cabeçalho do leitor de tela. O `scroll-mt`
      // existe para o título não ficar embaixo do topo fixo ao chegar pela âncora.
      return (
        <h3
          id={id}
          className="font-display max-w-[68ch] scroll-mt-24 pt-3 text-lg font-bold text-titulo"
        >
          <TextoRico texto={bloco.texto ?? ""} />
        </h3>
      );

    case "paragrafo":
      return (
        <p className="max-w-[68ch] text-[0.95rem] leading-relaxed text-tinta">
          <TextoRico texto={bloco.texto ?? ""} />
        </p>
      );

    case "destaque":
      return (
        <div
          className={`max-w-[68ch] rounded-xl border-l-4 p-4 text-sm leading-relaxed ${
            TONS[bloco.tom ?? "neutro"] ?? TONS.neutro
          }`}
        >
          <TextoRico texto={bloco.texto ?? ""} />
        </div>
      );

    case "lista": {
      const Lista = bloco.ordenada ? "ol" : "ul";
      return (
        <Lista
          className={`max-w-[68ch] space-y-1.5 pl-6 text-[0.95rem] leading-relaxed text-tinta ${
            bloco.ordenada ? "list-decimal" : "list-disc"
          }`}
        >
          {(bloco.itens ?? []).map((item, i) => (
            <li key={i}>
              <TextoRico texto={item} />
            </li>
          ))}
        </Lista>
      );
    }

    case "imagem":
      return (
        <figure>
          {/* `loading="lazy"` porque uma página chega a ter 15 imagens, e o fundo
              claro atrás do print evita a moldura escura em volta de screenshot
              de site do governo, que é quase sempre branco. */}
          <img
            src={urlDaImagem(bloco.imagem_path ?? "")}
            alt={bloco.imagem_alt ?? ""}
            loading="lazy"
            className="max-w-full rounded-xl border border-borda bg-white"
          />
          {bloco.texto && (
            <figcaption className="mt-2 max-w-[68ch] text-sm text-cinza">
              <TextoRico texto={bloco.texto} />
            </figcaption>
          )}
        </figure>
      );

    case "video":
      return (
        <figure>
          {/* `aspect-video` + `w-full`: iframe do YouTube com largura fixa estoura
              no celular, e é onde o aluno assiste. */}
          <div className="aspect-video max-w-[68ch] overflow-hidden rounded-xl bg-navy-ink">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${bloco.video_id}`}
              title={bloco.texto ?? "Vídeo da aula"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              className="size-full border-0"
            />
          </div>
          {bloco.texto && (
            <figcaption className="mt-2 max-w-[68ch] text-sm text-cinza">{bloco.texto}</figcaption>
          )}
        </figure>
      );

    default:
      // Tipo novo no banco e front antigo: não desenha nada, e não estoura. O
      // aluno vê o artigo sem aquele bloco em vez de uma tela branca — que é o
      // que `default: throw` daria.
      return null;
  }
}
